/**
 * Progress / section badges for Form.io forms.
 * Counts ONLY visible leaf inputs the user can fill.
 * Never counts: containers, hidden fields, calculated fields, layout wrappers.
 */

const LAYOUT_SKIP = new Set([
  'button',
  'hidden',
  'htmlelement',
  'content',
  'datasource',
  'panel',
  'fieldset',
  'well',
  'columns',
  'table',
  'tabs',
]);

/** Nest wrappers — count children, never the wrapper. */
const NEST_SKIP = new Set(['container', 'datagrid', 'editgrid', 'address']);

/** Calculated / composite phone keys — never progress fields. */
const SKIP_KEY_RE =
  /^(fullPhoneNumber|fullPhoneNumber1|fullMobileNumber|professionalPhoneNumber)$/i;

function getByPath(data, path) {
  if (!data || !path) return undefined;
  return String(path)
    .split('.')
    .reduce((acc, k) => (acc == null ? undefined : acc[k]), data);
}

/** Same visibility rules as guestTabCompletion / Form.io render. */
export function isComponentVisible(comp, rootData) {
  if (!comp || comp.hidden === true) return false;
  if (comp.type === 'hidden') return false;

  const cond = comp.conditional;
  if (cond && cond.when) {
    const actual = getByPath(rootData, cond.when);
    const eq = cond.eq;
    let match;
    if (Array.isArray(actual)) match = actual.map(String).includes(String(eq));
    else if (actual && typeof actual === 'object') match = actual[eq] === true;
    else match = String(actual) === String(eq);
    return cond.show === false ? !match : match;
  }

  if (typeof comp.customConditional === 'string' && comp.customConditional.trim()) {
    try {
      // eslint-disable-next-line no-new-func
      const fn = new Function(
        'data',
        'row',
        'component',
        'show',
        'utils',
        'instance',
        `${comp.customConditional}\n;return show;`
      );
      const result = fn(rootData, rootData, comp, true, {}, {});
      return result !== false;
    } catch (_) {
      return true;
    }
  }

  return true;
}

/**
 * True when a Form.io value counts as "user filled".
 * Phone containers: only digits / full number count — dial-code alone is NOT filled.
 */
export function isFormValueFilled(v) {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (typeof v === 'number') return !Number.isNaN(v);
  if (typeof v === 'boolean') return true;
  if (Array.isArray(v)) {
    return v.length > 0 && v.some((item) => isFormValueFilled(item));
  }
  if (typeof v === 'object') {
    if (Object.keys(v).length === 0) return false;

    // Phone / mobile containers — require actual number, not country dial code alone.
    if (
      'mobileTelephoneNumber' in v ||
      'phoneNumber1' in v ||
      'phoneNumberDigits' in v ||
      'fullPhoneNumber' in v ||
      'fullMobileNumber' in v ||
      'phoneNumber' in v
    ) {
      const digits =
        v.mobileTelephoneNumber ||
        v.phoneNumber1 ||
        v.phoneNumberDigits ||
        v.phoneNumber ||
        '';
      if (String(digits).trim()) return true;
      // Dial-code-only calculated values (e.g. "+1") must not count as filled.
      const full = String(v.fullPhoneNumber || v.fullMobileNumber || '').trim();
      if (!full || /^\+\d{1,4}$/.test(full)) return false;
      return true;
    }

    // Country / dial-code select objects (standalone country pickers).
    if ('dial_code' in v || 'code' in v || 'label' in v || 'value' in v) {
      return (
        isFormValueFilled(v.dial_code) ||
        isFormValueFilled(v.code) ||
        isFormValueFilled(v.value) ||
        isFormValueFilled(v.label) ||
        isFormValueFilled(v.name)
      );
    }

    return Object.values(v).some((child) => isFormValueFilled(child));
  }
  return false;
}

function safeForEachRow(rows, fn) {
  if (!Array.isArray(rows)) return;
  rows.forEach((row) => {
    if (Array.isArray(row)) row.forEach((cell) => fn(cell));
    else fn(row);
  });
}

/** Leaf inputs only — what the user actually sees and fills. */
function isCountableInput(comp) {
  if (!comp?.input || !comp?.key) return false;
  if (comp.hidden === true) return false;
  if (comp.type === 'hidden') return false;
  if (LAYOUT_SKIP.has(comp.type)) return false;
  if (NEST_SKIP.has(comp.type)) return false;
  if (SKIP_KEY_RE.test(comp.key)) return false;
  // Calculated fields are not user-entered — except interactive radios/selectboxes
  // (Invitation Types / Personal Settings use calculateValue but stay clickable).
  if (typeof comp.calculateValue === 'string' && comp.calculateValue.trim()) {
    const t = String(comp.type || '').toLowerCase();
    const interactiveOverride =
      (t === 'radio' || t === 'selectboxes') && comp.disabled !== true;
    if (!interactiveOverride) return false;
  }
  const cls = String(comp.customClass || '').toLowerCase();
  if (cls.includes('formio-hidden') || cls.includes('d-none') || cls.includes('hidden-field')) {
    return false;
  }
  return true;
}

function resolveDataValue(data, path, key) {
  if (!data || typeof data !== 'object') return undefined;
  if (path) {
    const nested = getByPath(data, path);
    if (nested !== undefined) return nested;
  }
  if (key && key in data) return data[key];
  return undefined;
}

/**
 * Collect visible leaf input keys (+ data paths) and optional panel → key map.
 * Prefer live Form.io visibility when formInstance is ready.
 */
export function collectVisibleInputKeys(data = {}, components = [], formInstance = null) {
  const entries = [];
  const keyToPanel = {};

  if (formInstance?.everyComponent) {
    formInstance.everyComponent((inst) => {
      const c = inst.component || {};
      if (!isCountableInput(c)) return;
      if (inst.visible === false) return;

      let parent = inst.parent;
      while (parent) {
        if (parent.visible === false) return;
        const pc = parent.component || {};
        if (pc.hidden === true || pc.type === 'hidden') return;
        parent = parent.parent;
      }

      let value;
      try {
        value = typeof inst.getValue === 'function' ? inst.getValue() : inst.dataValue;
      } catch (_) {
        value = undefined;
      }

      let panelKey = null;
      let panelInst = inst.parent;
      while (panelInst) {
        const ps = panelInst.component || {};
        if ((ps.type === 'panel' || ps.type === 'fieldset') && ps.key) {
          panelKey = ps.key;
          break;
        }
        panelInst = panelInst.parent;
      }

      entries.push({ key: c.key, path: c.key, panelKey, value });
      if (panelKey) keyToPanel[c.key] = panelKey;
    });
  } else {
    const walk = (comps, panelKey, dataPathPrefix) => {
      if (!Array.isArray(comps)) return;
      comps.forEach((c) => {
        if (!c) return;

        const isPanel = c.type === 'panel' || c.type === 'fieldset';
        const isNest = NEST_SKIP.has(c.type);
        const isLayoutOnly = LAYOUT_SKIP.has(c.type) && !isPanel;

        if (c.hidden === true || c.type === 'hidden') return;

        if (!isPanel && !isNest && !isLayoutOnly && !isComponentVisible(c, data)) return;
        if ((isPanel || isNest) && !isComponentVisible(c, data)) return;

        const nextPanel = isPanel && c.key ? c.key : panelKey;
        const childPrefix =
          isNest && c.key
            ? dataPathPrefix
              ? `${dataPathPrefix}.${c.key}`
              : c.key
            : dataPathPrefix;

        if (isCountableInput(c) && isComponentVisible(c, data)) {
          const path = dataPathPrefix ? `${dataPathPrefix}.${c.key}` : c.key;
          entries.push({
            key: c.key,
            path,
            panelKey,
            value: resolveDataValue(data, path, c.key),
          });
          if (panelKey) keyToPanel[c.key] = panelKey;
        }

        if (c.components) walk(c.components, nextPanel, childPrefix);
        if (c.columns) {
          c.columns.forEach((col) => walk(col.components, nextPanel, childPrefix));
        }
        if (c.rows) {
          safeForEachRow(c.rows, (cell) => {
            if (cell?.components) walk(cell.components, nextPanel, childPrefix);
          });
        }
        if (c.datagrid?.components) walk(c.datagrid.components, nextPanel, childPrefix);
        if (c.editgrid?.components) walk(c.editgrid.components, nextPanel, childPrefix);
      });
    };
    walk(components, null, '');
  }

  const byKey = new Map();
  entries.forEach((e) => byKey.set(e.key, e));
  const uniqueEntries = [...byKey.values()];
  return {
    keys: uniqueEntries.map((e) => e.key),
    keyToPanel,
    entries: uniqueEntries,
  };
}

/**
 * @returns {{ filled: number, total: number, percentage: number, unique: string[], byPanel: Record<string, { filled: number, total: number, percentage: number }> }}
 */
export function calculateVisibleFieldProgress(data = {}, components = [], formInstance = null) {
  const { keys: uniqueKeys, keyToPanel, entries } = collectVisibleInputKeys(
    data,
    components,
    formInstance
  );

  const isFilledEntry = (entry) => {
    if (entry.value !== undefined) return isFormValueFilled(entry.value);
    return isFormValueFilled(resolveDataValue(data, entry.path, entry.key));
  };

  const total = uniqueKeys.length;
  const filled = entries.filter(isFilledEntry).length;

  const byPanel = {};
  entries.forEach((entry) => {
    const panelKey = entry.panelKey || keyToPanel[entry.key];
    if (!panelKey) return;
    if (!byPanel[panelKey]) byPanel[panelKey] = { filled: 0, total: 0 };
    byPanel[panelKey].total += 1;
    if (isFilledEntry(entry)) byPanel[panelKey].filled += 1;
  });

  Object.keys(byPanel).forEach((panelKey) => {
    const c = byPanel[panelKey];
    c.percentage = c.total > 0 ? Math.round((c.filled / c.total) * 100) : 100;
  });

  return {
    filled,
    total,
    percentage: total > 0 ? Math.round((filled / total) * 100) : 100,
    unique: uniqueKeys,
    byPanel,
  };
}
